/**
 * Утилиты онлайн-статуса и typing-индикатора.
 *
 *  - isOnline: «в сети» если last_seen_at в пределах ONLINE_WINDOW_MS
 *  - fmtLastSeen: «в сети» / «был в сети 5 мин назад» / «… 2 ч назад» / «вчера»
 *  - fmtTyping: 1 → «X печатает…», 2 → «X и Y печатают…», 3+ → «N человек(а) печатают…»
 */

// Окно «онлайн». Считаем юзера онлайн только если last_seen_at в этом окне.
// 75s даёт буфер: chat-poll топиков идёт каждые 10s + явный heartbeat
// раз в 30s (AppShell). При закрытии вкладки фронт сразу шлёт sendBeacon
// на /api/profiles/offline → юзер становится offline за секунды.
export const ONLINE_WINDOW_MS = 75 * 1000;


/**
 * Backend отдаёт naive UTC ISO (без 'Z'), часто в формате с пробелом
 * вместо 'T'. Без 'Z' браузер парсит как LOCAL TIME → в Москве (UTC+3) diff
 * всегда +3 часа (отсюда «3 часа назад» у всех). Этот хелпер всегда даёт
 * корректный UTC Date.
 */
export function parseBackendIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  if (iso.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(iso)) return new Date(iso);
  const normalized = iso.includes("T") ? iso : iso.replace(" ", "T");
  return new Date(normalized + "Z");
}

function parseUtc(iso: string | null | undefined): Date | null {
  return parseBackendIso(iso);
}


export function isOnline(lastSeenAt: string | null | undefined): boolean {
  const d = parseUtc(lastSeenAt);
  if (!d) return false;
  return Date.now() - d.getTime() < ONLINE_WINDOW_MS;
}


export function fmtLastSeen(lastSeenAt: string | null | undefined): string {
  const d = parseUtc(lastSeenAt);
  if (!d) return "не заходил";
  const diffMs = Date.now() - d.getTime();
  if (diffMs < ONLINE_WINDOW_MS) return "в сети";
  const min = Math.floor(diffMs / 60000);
  if (min < 60) return `был в сети ${min} ${pluralRu(min, ["минуту", "минуты", "минут"])} назад`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `был в сети ${hours} ${pluralRu(hours, ["час", "часа", "часов"])} назад`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `был в сети ${days} ${pluralRu(days, ["день", "дня", "дней"])} назад`;
  return `был в сети ${d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" })}`;
}


/** Русские склонения по числу (стандартная функция). */
export function pluralRu(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n) % 100;
  const n1 = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (n1 > 1 && n1 < 5) return forms[1];
  if (n1 === 1) return forms[0];
  return forms[2];
}


export interface TypingDisplay { user_id: string; display_name: string }

export function fmtTyping(users: TypingDisplay[]): string | null {
  if (users.length === 0) return null;
  // Используем только первое имя (без фамилии) чтобы строка не была длинной
  const fn = (u: TypingDisplay) => (u.display_name || "?").split(" ")[0];
  if (users.length === 1) return `${fn(users[0])} печатает…`;
  if (users.length === 2) return `${fn(users[0])} и ${fn(users[1])} печатают…`;
  if (users.length === 3) return `${fn(users[0])}, ${fn(users[1])} и ${fn(users[2])} печатают…`;
  return `${users.length} ${pluralRu(users.length, ["пользователь", "пользователя", "пользователей"])} печатают…`;
}
